//#region imports

// react
import React, { useState, useEffect, use } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { useHistory } from 'react-router-dom';

// redux
import { useSelector } from 'react-redux';
import { selectShoppingCart, selectShoppingCartPrice, selectTableNumber, setTableNumber, setManuallySet } from '_redux/slices/marketplace/user-marketplace';
import { selectOneRetailer } from '_redux/slices/marketplace/retailer';

// custom components
import { Modal } from '_atoms';
import { CartCard } from '_components/molecules';
import CheckoutForm from '../checkout-form';
import ChangeTableModal from './change-table-modal';
import MarketplaceFooter from './marketplace-footer';
import ProductModal from './product-modal';
import CheckoutPricing from './checkout-pricing';
import { toast, ToastContainer } from 'react-toastify';

// styles
import { Spacing, Colors } from '_styles';

// bootstrap
import Button from 'react-bootstrap/Button';

// custom components
import RetailerStatus from '../components/retailer-status';
import RetailerTitle from '../components/retailer-title';

//#endregion imports

const CheckoutInformation = () => {

    const history = useHistory();

    //#region redux
    const cart = useSelector(selectShoppingCart) || {};
    const totalPrice = useSelector(selectShoppingCartPrice);
    const tableNumber = useSelector(selectTableNumber);
    const retailerInfo = useSelector(selectOneRetailer);

    //#endregion redux

    //#region local states
    const [showCheckoutForm, setShowCheckoutForm] = useState(false);
    const [showChangeTableModal, setShowChangeTableModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    //#endregion local states

    //#region handlers
    const handleChangeTable = () => setShowChangeTableModal(true);

    // handle logo click to go to retailer products page
    const handleLogoClick = () => {
        // go to retailer products page
        history.push(`/retailer-products/${retailerInfo.id}?tableNumber=${tableNumber}`);
    }
    //#endregion handlers

    //#region styles
    const styles = {
        container: {
            display: 'flex',
            flexDirection: 'column',
        },
        mainContent: {
            flex: 1,
            overflowY: 'auto',
            backgroundColor: '#fff',
        },
        title: {
            fontSize: 28,
            fontWeight: 800,
            color: '#333',
        },
        subtitle: {
            fontSize: 24,
            fontWeight: 800,
            color: '#333',
            marginLeft: Spacing.SCALE_16,
            marginTop: Spacing.SCALE_16,
        },
        content: {
            flex: 1,
        },
        cardWrapper: {
            marginBottom: Spacing.SCALE_16,
        },
        purchaseContainer: {
            backgroundColor: '#F0F0F0',
            borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
            padding: `${Spacing.SCALE_16}px ${Spacing.SCALE_20}px`,
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        label: {
            fontSize: 16,
            fontWeight: 600,
            color: '#333',
        },
        labelValue: {
            fontWeight: 'normal',
            marginLeft: 4,
            color: '#333',
        },
        button: {
            minWidth: '100%',
            marginTop: Spacing.SCALE_16,
            padding: `${Spacing.SCALE_10}px ${Spacing.SCALE_20}px`,
            fontWeight: 'bold',
        },
        accordionHeader: {
            fontSize: 20,
            fontWeight: 700,
            color: '#333',
        },
    };
    //#endregion styles

    //#region useEffect
    useEffect(() => {
        // if cart is empty, redirect to homepage after 10 seconds
        if (Object.keys(cart).length === 0) {
            toast.info("Your cart is empty. You will be redirected shortly.", {
                onClose: () => window.history.back(),
            });
        }
    }, [cart, history]);


    //#endregion useEffect

    return (
        <div style={styles.container}>

            {/* list items in cart */}
            <div style={styles.mainContent}>

                {/* page header */}
                <RetailerTitle retailer={retailerInfo} handleLogoClick={handleLogoClick} hideCart={true} />

                {/* product list */}
                <div style={{ padding: Spacing.SCALE_22 }}>

                    {/* title */}
                    <p style={styles.title}>Order Summary</p>

                    {/* table number */}
                    <p style={styles.label}>Delivery Point</p>

                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: "space-between", padding: Spacing.SCALE_22, backgroundColor: Colors.GRAY_LIGHT, borderRadius: 10 }}>
                        <div style={styles.label}>Table Number: {tableNumber}</div>
                        <a href='#' onClick={handleChangeTable} style={{ color: Colors.PRM_LIGHT_BLUE, textDecoration: 'underline', cursor: 'pointer' }}>
                            Change Table
                        </a>
                    </div>

                    {/* title */}
                    <div className='mt-2'>
                        <p style={styles.label}>Your Items</p>

                        <div style={styles.content}>

                            {/* map each item */}
                            {Object.values(cart).length > 0 ? (Object.values(cart).map((item) => (

                                /* cart item card */
                                <div key={item.id} style={styles.cardWrapper} onClick={() => setSelectedItem(item)}>
                                    {/* product info + add quantity modal */}
                                    <CartCard item={item} isCheckout={true} />
                                </div>
                            )))
                                :
                                (
                                    /* no items in the cart */
                                    <>
                                        <p style={{ color: 'black' }}>Your cart is empty.</p>
                                        <ToastContainer />
                                    </>
                                )}
                        </div>
                    </div>
                </div>

                {/* terms and conditions */}
                <div style={{ textAlign: 'center', marginTop: '1em', padding: Spacing.SCALE_12 }}>
                    <p style={{ fontSize: '14px', color: '#666' }}>
                        By continuing, you agree with our privacy policy and{' '}
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                history.push('/terms-and-conditions');
                            }}
                            style={{ color: '#007BFF', textDecoration: 'underline' }}
                        >
                            Terms and Conditions
                        </a>.
                    </p>
                </div>

                {/* purchase summary + complete purchase button */}
                <div style={styles.purchaseContainer}>

                    <CheckoutPricing
                        price={totalPrice}
                    />

                    {/* complete purchase button */}
                    <Button
                        style={styles.button}
                        variant="primary"
                        onClick={() => setShowCheckoutForm(true)}
                    >Complete Purchase</Button>

                    {showChangeTableModal && (
                        <Modal>
                            <ChangeTableModal done={() => setShowChangeTableModal(false)} />
                        </Modal>
                    )}

                </div>

            </div>

            {/* footer */}
            <MarketplaceFooter />

            {/* Modals */}
            {selectedItem && (
                <Modal>
                    <ProductModal
                        product={selectedItem}
                        onClose={() => setSelectedItem(null)}
                        modalOpen={true}
                        origin="Order Summary"
                    />
                </Modal>
            )}


            {/* checkout modal */}
            {showCheckoutForm &&
                <Modal>
                    <CheckoutForm onClose={() => setShowCheckoutForm(false)} />
                </Modal>
            }

            {

            }
        </div>
    );
};

export default CheckoutInformation;
